import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  CardContent,
  Stack,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import tecnicoService, { TecnicoReadDto } from '../../services/tecnicoService';

const Tecnicos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tecnicos, setTecnicos] = useState<TecnicoReadDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const goNuevo = useCallback(() => {
    navigate('/servicio/tecnicos/nuevo');
  }, [navigate]);

  const fetchTecnicos = useCallback(
    async (pageNum: number, pageSize: number, search: string) => {
      try {
        setLoading(true);
        const data = await tecnicoService.getTecnicos({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
        });
        setTecnicos(data.items);
        setTotalCount(data.totalCount);
      } catch (error: unknown) {
        console.error('Error al cargar técnicos:', error);
        enqueueSnackbar('Error al cargar técnicos', { variant: 'error' });
        setTecnicos([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  // Una sola carga: evita el doble GET del mount + searchTerm.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTecnicos(page, rowsPerPage, searchTerm);
    }, searchTerm ? 300 : 0);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, fetchTecnicos]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Insert' && e.key !== 'F2') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
        if (e.key === 'F2') return;
      }
      e.preventDefault();
      goNuevo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNuevo]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleDelete = async (codigoTecnico: string) => {
    if (!window.confirm('¿Está seguro de eliminar este técnico?')) return;
    try {
      await tecnicoService.deleteTecnico(codigoTecnico);
      enqueueSnackbar('Técnico eliminado exitosamente', { variant: 'success' });
      fetchTecnicos(page, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      console.error('Error al eliminar técnico:', error);
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al eliminar técnico';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleEdit = (tec: TecnicoReadDto) => {
    const codigo = (tec.codigoTecnico || '').trim();
    if (codigo) navigate(`/servicio/tecnicos/${encodeURIComponent(codigo)}`);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Técnicos
            </Typography>
            <Tooltip title="Insert o F2">
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={goNuevo}>
                Nuevo Técnico
              </Button>
            </Tooltip>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por código o nombre..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography color="text.secondary">Cargando...</Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {tecnicos.map((tec) => {
                const codigo = (tec.codigoTecnico || '').trim();
                const desc = (tec.descripcion || '').trim();
                return (
                  <Card key={codigo} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {codigo} - {desc}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton size="small" onClick={() => handleEdit(tec)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(codigo)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código Técnico</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tecnicos.map((tec) => {
                    const codigo = (tec.codigoTecnico || '').trim();
                    const desc = (tec.descripcion || '').trim();
                    return (
                      <TableRow key={codigo} hover>
                        <TableCell>{codigo}</TableCell>
                        <TableCell>{desc}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleEdit(tec)} color="primary">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => handleDelete(codigo)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Tecnicos;
